import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  CssBaseline,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import CustomBottomNav from '../Buy Module/CustomNav'; // Adjust the import path if needed
import backgroundImage from '../../../../src/Images/explore-bg-image.jpeg'; // adjust path if needed

// ---------------------------------------------------------------------------
// Responsive helpers
// Everything is sized with clamp()/vw/aspect-ratio so the layout scales
// proportionally on iPhone SE, iPhone 12, Galaxy S8+, Pixel, small tablets etc.
// ---------------------------------------------------------------------------
const SIDE_GAP = 'clamp(12px, 4.5vw, 28px)';
const CARD_GAP = 'clamp(10px, 3.2vw, 18px)';
const CARD_RADIUS = 'clamp(18px, 5.5vw, 28px)';
const BOTTOM_NAV_SPACE = 'calc(96px + env(safe-area-inset-bottom, 0px))';

// --- Mock data ---
const mapMarkers = [
  { top: '15%', left: '22%' },
  { top: '14%', left: '78%' },
  { top: '40%', left: '16%' },
  { top: '45%', left: '50%', central: true },
  { top: '42%', left: '86%' },
  { top: '65%', left: '75%' },
  { top: '75%', left: '30%' },
  { top: '88%', left: '85%' },
];

const listProperties = [
  { id: 1, price: '₹3.20 Cr', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=300&q=80' },
  { id: 2, price: '₹2.50 Cr', image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=300&q=80' },
  { id: 3, price: '₹1.00 Cr', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=300&q=80' },
];

// Shared styles
const cardSx = {
  flex: '1 1 0',
  minWidth: 0, // prevents overflow on narrow screens
  borderRadius: CARD_RADIUS,
  overflow: 'hidden',
  boxShadow: '0 10px 24px rgba(0,0,0,0.10)',
  display: 'flex',
  flexDirection: 'column',
};

// Same aspect ratio for both graphic areas => identical height on every device
const graphicAreaSx = {
  width: '100%',
  aspectRatio: '3 / 4',
  position: 'relative',
  overflow: 'hidden',
};

const footerSx = {
  backgroundColor: '#8B4F16',
  color: '#fff',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 1,
  py: 'clamp(14px, 4vw, 22px)',
  px: 'clamp(12px, 3.6vw, 20px)',
  '@media (hover: hover)': {
    '&:hover': { backgroundColor: '#4E342E' },
  },
};

const footerTitleSx = {
  fontWeight: 700,
  fontSize: 'clamp(1rem, 4.8vw, 1.5rem)',
  lineHeight: 1.15,
};

const footerCaptionSx = {
  display: 'block',
  opacity: 0.85,
  fontSize: 'clamp(0.68rem, 2.9vw, 0.9rem)',
  lineHeight: 1.3,
  mt: 0.5,
};

const arrowBtnSx = {
  bgcolor: '#fff',
  color: '#8B4F16',
  flexShrink: 0,
  width: 'clamp(30px, 9vw, 44px)',
  height: 'clamp(30px, 9vw, 44px)',
  '&:hover': { bgcolor: '#f0f0f0' },
};

const BuyPropertyExplorer = () => {
  const navigate = useNavigate();

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
          position: 'relative',
          overflowX: 'hidden',
          pb: BOTTOM_NAV_SPACE, // room for the bottom nav + iOS home indicator
          backgroundColor: '#FCF3EA',

          // Background image on a fixed layer
          // (background-attachment: fixed is buggy on iOS Safari)
          '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        {/* ---------------- Header ---------------- */}
        <Box
          sx={{
            px: SIDE_GAP,
            pt: 'calc(clamp(12px, 4vw, 24px) + env(safe-area-inset-top, 0px))',
            pb: 'clamp(28px, 9vh, 80px)',
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              mb: 'clamp(8px, 3vh, 24px)',
              ml: -1,
              color: '#3E2723',
              '&:hover': { backgroundColor: 'rgba(62, 39, 35, 0.08)' },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography
            component="h1"
            sx={{
              fontWeight: 400,
              color: '#111',
              fontSize: 'clamp(2rem, 11vw, 3.6rem)',
              lineHeight: 1.05,
              m: 0,
            }}
          >
            Find Your
            <Box
              component="span"
              sx={{
                display: 'block',
                fontWeight: 800,
                color: '#8B4F16',
                fontSize: 'inherit',
                lineHeight: 1.05,
              }}
            >
              Property
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 'clamp(6px, 2vw, 14px)',
              color: '#757575',
              fontWeight: 500,
              fontSize: 'clamp(0.9rem, 4.2vw, 1.3rem)',
              maxWidth: '65%',
              lineHeight: 1.35,
            }}
          >
            Choose how you want to explore
          </Typography>
        </Box>

        {/* ---------------- Cards ---------------- */}
        <Box
          sx={{
            px: SIDE_GAP,
            display: 'flex',
            gap: CARD_GAP,
            alignItems: 'stretch',
            maxWidth: 720,
            mx: 'auto',
            boxSizing: 'border-box',
            paddingTop: '20%',
          }}
        >
          {/* ---- Map View ---- */}
          <Card sx={cardSx}>
            <Box
              sx={{
                ...graphicAreaSx,
                backgroundColor: '#EEF1EC',
                backgroundImage:
                  'linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            >
              {/* abstract map blocks (percent based => scale with card) */}
              <Box sx={{ position: 'absolute', top: '12%', left: '18%', width: '42%', height: '24%', bgcolor: '#D6E6F2', borderRadius: 2 }} />
              <Box sx={{ position: 'absolute', top: '58%', left: '12%', width: '26%', height: '28%', bgcolor: '#DCE6DC', borderRadius: 2 }} />
              <Box sx={{ position: 'absolute', top: '50%', left: '62%', width: '26%', height: '22%', bgcolor: '#DCE6DC', borderRadius: 2 }} />

              {mapMarkers.map((marker, index) => (
                <HomeIcon
                  key={index}
                  sx={{
                    position: 'absolute',
                    top: marker.top,
                    left: marker.left,
                    color: '#8B4F16',
                    fontSize: marker.central
                      ? 'clamp(36px, 12vw, 60px)'
                      : 'clamp(22px, 7vw, 36px)',
                    transform: 'translate(-50%, -50%)',
                    zIndex: marker.central ? 2 : 1,
                    filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.2))',
                  }}
                />
              ))}
            </Box>

            <CardActionArea onClick={() => navigate('/buy-map')} sx={{ mt: 'auto' }}>
              <CardContent sx={footerSx}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={footerTitleSx}>Map View</Typography>
                  <Typography component="span" sx={footerCaptionSx}>
                    Explore properties on map
                  </Typography>
                </Box>
                <IconButton component="span" size="small" sx={arrowBtnSx}>
                  <ArrowForwardIcon sx={{ fontSize: 'clamp(16px, 5vw, 24px)' }} />
                </IconButton>
              </CardContent>
            </CardActionArea>
          </Card>

          {/* ---- List View ---- */}
          <Card sx={cardSx}>
            <Box
              sx={{
                ...graphicAreaSx,
                backgroundColor: '#fff',
                p: 'clamp(8px, 2.6vw, 14px)',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-evenly',
              }}
            >
              {listProperties.map((property) => (
                <Box
                  key={property.id}
                  sx={{
                    display: 'flex',
                    gap: 'clamp(6px, 2vw, 12px)',
                    alignItems: 'center',
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={property.image}
                    alt="property"
                    loading="lazy"
                    sx={{
                      width: '40%',
                      aspectRatio: '1.1 / 1',
                      flexShrink: 0,
                      borderRadius: 'clamp(6px, 2vw, 12px)',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ height: 'clamp(5px, 1.8vw, 9px)', width: '90%', bgcolor: '#E3E3E3', borderRadius: 1, mb: '5px' }} />
                    <Box sx={{ height: 'clamp(5px, 1.8vw, 9px)', width: '100%', bgcolor: '#E3E3E3', borderRadius: 1, mb: '5px' }} />
                    <Box sx={{ height: 'clamp(5px, 1.8vw, 9px)', width: '55%', bgcolor: '#E3E3E3', borderRadius: 1, mb: 'clamp(4px, 1.5vw, 8px)' }} />
                    <Typography
                      noWrap
                      sx={{
                        color: '#5D4037',
                        fontWeight: 700,
                        fontSize: 'clamp(0.7rem, 3.2vw, 1rem)',
                      }}
                    >
                      {property.price}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            <CardActionArea onClick={() => navigate('/Buy-details')} sx={{ mt: 'auto' }}>
              <CardContent sx={footerSx}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={footerTitleSx}>List View</Typography>
                  <Typography component="span" sx={footerCaptionSx}>
                    Browse properties in a list
                  </Typography>
                </Box>
                <IconButton component="span" size="small" sx={arrowBtnSx}>
                  <ArrowForwardIcon sx={{ fontSize: 'clamp(16px, 5vw, 24px)' }} />
                </IconButton>
              </CardContent>
            </CardActionArea>
          </Card>
        </Box>
      </Box>

      {/* Bottom navigation from your existing project */}
      <CustomBottomNav />
    </>
  );
};

export default BuyPropertyExplorer;